import React, { useEffect, useState, forwardRef, createRef } from "react";
import { useTheme } from "react-native-paper";
import { useSelector, useDispatch } from "react-redux";

import { View, StyleSheet, Platform } from "react-native";
import {
  Screen,
  Button,
  Text,
  TextInput,
  InfoBox,
  InputAccessoryNavigation,
} from "../../components";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "../../components/layout/stack";
import { useNavigation } from "@react-navigation/native";
import { ThemeType } from "../../theme/theme";
import { useForm } from "react-hook-form";
import { translate } from "../../i18n";
import { USStateDropdown, MonthYearDropdown } from "../../components/dropdown";
import { DomusoApiService } from "../../api/DomusoApiService";

import { verifyBillingAddressSchema } from "../wallet/schemas";
import { Spacer } from "../../components/spacer/spacer";
import { ErrorMessage } from "../../components/error-message/error-message";
import moment from "moment";
import { iconSize } from "../../theme/iconSize";
import { CreditCardFields, InfoBoxIconType, InfoBoxType } from "../../enums";
import { RootState } from "../../redux/reducers";
import { testProps } from "../../utils/testProps";
import { ERROR_CODES, getErrorMessageId } from "../../utils/errorCodes";

const { CVV, STREET_ADDRESS, STREET_ADDRESS_2, CITY, STATE, EXP_DATE, ZIP } = CreditCardFields;

interface IProps {
  route: any;
}

export const VerifyBillingAddress = ({ route }: IProps) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const theme = useTheme() as ThemeType;
  const styles = getStyles(theme);
  const [loading, setLoading] = useState(false);
  const { params = {} } = route;
  const { fromSelectCertifiedPayment } = params;
  const cognitoUser = useSelector((state) => state.auth.cognitoUser);
  const [apiError, setApiError] = useState<any>(false);
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(verifyBillingAddressSchema),
  });
  const service = new DomusoApiService(cognitoUser, dispatch);
  const logger = useSelector((state: RootState) => state.logger);

  useEffect(() => {
    register(STREET_ADDRESS);
    register(STREET_ADDRESS_2);
    register(ZIP);
    register(CITY);
    register(STATE);
  }, [register]);

  const inputAccessoryViewID = "inputAccessoryViewID6";
  const ref = [createRef(), createRef(), createRef(), createRef(), createRef()];
  const [currentInputIndex, setCurrentInputIndex] = useState(0);

  const onSubmit = async (data: any) => {
    setLoading(true);

    try {
      const { streetAddress, streetAddress2, city, state, zip } = data;
      const { paymentSourceId, currentProperty } = params;

      const avsResponse = await service.verifyBillingAddress({
        property_id: currentProperty.id,
        payment_source_id: paymentSourceId,
        street_address: streetAddress,
        address_line2: streetAddress2,
        city,
        state,
        zip,
      });

      setLoading(false);

      return navigation.navigate("ReviewPayment", {
        ...params,
      });
    } catch (e) {
      await logger.error({ e });
      await logger.error(e);
      const { code } = e;
      const messageId = getErrorMessageId(e);

      if (
        [
          ERROR_CODES.CREDIT_CARD_INVALID_BILLING_ADDRESS_ERROR,
          ERROR_CODES.CREDIT_CARD_INVALID_BILLING_ZIP_ERROR,
        ].includes(code) ||
        !messageId
      ) {
        setApiError(translate("homeStack.avs.generic"));
      } else {
        setApiError(translate(messageId));
      }
      setLoading(false);
    }
  };

  return (
    <Screen
      style={styles.container}
      preset="scroll"
      statusBar="dark-content"
      dontUseAutoPadding={true}
      keyboardOffset="keyboardNavigation"
      backgroundColor={theme.colors.background}>
      <Stack style={{ paddingHorizontal: theme.spacing.medium }}>
        <Text tx="homeStack.billingAddressHint" style={styles.sectionHint} />
      </Stack>
      <Stack space="none" horizontalSpace="medium">
        <Text tx="homeStack.billingAddress" style={styles.sectionTitle} />
        <LongRow
          styles={styles}
          errors={errors}
          setValue={setValue}
          maxLength={35}
          formName={STREET_ADDRESS}
          returnKeyType="next"
          inputAccessoryViewID={inputAccessoryViewID}
          ref={ref[0] as any}
          onFocus={() => setCurrentInputIndex(0)}
          onSubmitEditing={() => (ref[1].current as any).focus()}
        />
        <LongRow
          styles={styles}
          errors={errors}
          setValue={setValue}
          formName={STREET_ADDRESS_2}
          returnKeyType="next"
          inputAccessoryViewID={inputAccessoryViewID}
          ref={ref[1] as any}
          onFocus={() => setCurrentInputIndex(1)}
          onSubmitEditing={() => (ref[2].current as any).focus()}
        />
        <LongRow
          keyboardType="number-pad"
          errors={errors}
          styles={styles}
          setValue={setValue}
          formName={ZIP}
          returnKeyType="next"
          inputAccessoryViewID={inputAccessoryViewID}
          ref={ref[2] as any}
          onFocus={() => setCurrentInputIndex(2)}
          onSubmitEditing={() => (ref[3].current as any).focus()}
        />
        <CityStateRow
          styles={styles}
          setValue={setValue}
          setError={setError}
          errors={errors}
          inputAccessoryViewID={inputAccessoryViewID}
          ref={[ref[3], ref[4]] as any}
          onFocus0={() => setCurrentInputIndex(3)}
          onSubmitEditing0={() => (ref[4].current as any).focus()}
          onFocus1={() => setCurrentInputIndex(4)}
          onSubmitEditing1={handleSubmit(onSubmit)}
        />

        {Platform.OS === "ios" ? (
          <InputAccessoryNavigation
            inputAccessoryViewID={inputAccessoryViewID}
            ref={ref as any}
            currentInputIndex={currentInputIndex}
          />
        ) : null}
        {apiError ? (
          <InfoBox
            width="100%"
            headline={translate("homeStack.error")}
            infoBoxType={InfoBoxType.RED}
            usesXClose={true}
            style={styles.billingAddressError}
            xCloseHandler={() => setApiError(null)}
            headlineIconType={InfoBoxIconType.RED_X}>
            <Text>{apiError}</Text>
          </InfoBox>
        ) : (
          <Spacer size="larger" />
        )}

        <Spacer size="larger" />
        <Stack style={{ paddingHorizontal: theme.spacing.small }}>
          <Button
            loading={loading}
            disabled={loading}
            mode="contained"
            tx="homeStack.continue"
            {...testProps("submit-button")}
            onPress={handleSubmit(onSubmit)}
          />
        </Stack>
      </Stack>
    </Screen>
  );
};

interface LongRowProps {
  styles: any;
  errors?: Record<string, any>;
  setValue: any;
  formName: string;
  maxLength?: number;
  render?: any;
  keyboardType?: any;
  placeholder?: string;
  returnKeyType?:
    | "default"
    | "none"
    | "done"
    | "go"
    | "next"
    | "search"
    | "send"
    | "previous"
    | "google"
    | "join"
    | "route"
    | "yahoo"
    | "emergency-call"
    | undefined;
  inputAccessoryViewID?: string;
  onFocus?: any;
  onSubmitEditing?: any;
}

const LongRow = forwardRef(
  (
    {
      styles,
      setValue,
      formName,
      placeholder,
      errors,
      render,
      maxLength,
      keyboardType = "default",
      returnKeyType,
      inputAccessoryViewID,
      onFocus,
      onSubmitEditing,
    }: LongRowProps,
    ref: any
  ) => {
    const theme = useTheme() as ThemeType;
    return (
      <View>
        <Text tx={`walletStack.${formName}`} style={styles.label} />
        <TextInput
          error={errors?.[formName]?.message}
          mode="outlined"
          render={render}
          keyboardType={keyboardType}
          maxLength={maxLength}
          placeholder={placeholder}
          onChangeText={(text) => setValue(formName, text)}
          ref={ref}
          returnKeyType={returnKeyType}
          inputAccessoryViewID={inputAccessoryViewID}
          onFocus={onFocus}
          onSubmitEditing={onSubmitEditing}
        />
        <ErrorMessage errors={errors} formName={formName} />
      </View>
    );
  }
);

interface CityStateRowProps {
  setValue: Function;
  setError: Function;
  styles: any;
  errors: any;
  inputAccessoryViewID?: string;
  onFocus0?: any;
  onSubmitEditing0?: any;
  onFocus1?: any;
  onSubmitEditing1?: any;
}

const CityStateRow = forwardRef(
  (
    {
      setValue,
      setError,
      styles,
      errors,
      inputAccessoryViewID,
      onFocus0,
      onSubmitEditing0,
      onFocus1,
      onSubmitEditing1,
    }: CityStateRowProps,
    ref: any
  ) => {
    const theme = useTheme() as ThemeType;
    return (
      <>
        <View>
          <Text tx="walletStack.city" style={styles.label} />
          <TextInput
            error={errors?.[CITY]?.message}
            mode="outlined"
            onChangeText={(text) => setValue(CITY, text)}
            ref={ref[0]}
            inputAccessoryViewID={inputAccessoryViewID}
            onFocus={onFocus0}
            onSubmitEditing={onSubmitEditing0}
            returnKeyType="next"></TextInput>
          <ErrorMessage errors={errors} formName={CITY} />
        </View>
        <View>
          <Text tx="walletStack.state" style={styles.label} />
          <USStateDropdown
            setValue={(value) => setValue(STATE, value)}
            formName={STATE}
            setError={setError}
            errors={errors}
            ref={ref[1]}
            inputAccessoryViewID={inputAccessoryViewID}
            onFocus={onFocus1}
            onSubmitEditing={onSubmitEditing1}
          />
          <ErrorMessage errors={errors} formName={STATE} />
        </View>
      </>
    );
  }
);

interface CardInformationRowProps {
  setValue: Function;
  setError: Function;
  clearErrors: Function;
  expDate: Date;
  styles: any;
  errors: any;
}
const CardInformationRow = ({
  setValue,
  setError,
  styles,
  errors,
  clearErrors,
}: CardInformationRowProps) => {
  const setExpDate = (formName: string, value: any) => {
    clearErrors(formName);
    const now = moment().startOf("month");
    const selected = value.startOf("month");
    const formatted = value.format("MMYY");
    setValue(formName, formatted);
    if (selected < now) {
      setError(formName, { type: "invalid", message: "Invalid expiry date" });
    }
  };

  return (
    <View style={styles.cardInformationRow}>
      <View style={{ width: "48%" }}>
        <Text tx="walletStack.expirationDate" style={styles.label} />
        <View style={styles.expDate}>
          <MonthYearDropdown
            setValue={setExpDate}
            formName={EXP_DATE}
            setError={setError}
            errors={errors}
          />
          <ErrorMessage errors={errors} formName={EXP_DATE} />
        </View>
      </View>
      <View style={{ width: "48%" }}>
        <Text tx="walletStack.cvv" style={styles.label} />
        <TextInput
          error={errors?.[CVV]?.message}
          keyboardType="number-pad"
          maxLength={3}
          mode="outlined"
          onChangeText={(text) => setValue(CVV, text)}
        />
        <ErrorMessage errors={errors} formName={CVV} />
      </View>
    </View>
  );
};

const getStyles = (theme: ThemeType) => {
  const text = { color: theme.colors.surface };
  const { colors, spacing, fontSize } = theme;
  return StyleSheet.create({
    container: {
      backgroundColor: colors.background,
      paddingTop: spacing.large,
      paddingBottom: spacing.large,
    },
    ccContainer: { marginLeft: "auto", marginRight: "auto", marginBottom: theme.spacing.smaller },
    ccImage: {
      width: iconSize.larger,
      height: 45,
      borderRadius: 5,
    },
    sectionTitle: {
      fontSize: fontSize.large,
      textTransform: "uppercase",
      fontWeight: "bold",
      marginTop: spacing.small,
      marginBottom: spacing.tiny,
      paddingTop: spacing.tiny,
      paddingBottom: spacing.tiny,
      backgroundColor: colors.lightGrey,
    },
    sectionHint: {
      fontSize: fontSize.large,
      marginTop: spacing.tiny,
      marginBottom: spacing.tiny,
      paddingTop: spacing.tiny,
      paddingBottom: spacing.none,
    },
    label: {
      fontSize: fontSize.large,
      marginLeft: spacing.smaller,
      marginBottom: spacing.tiny,
      marginTop: spacing.small,
    },
    dateFields: {
      display: "flex",
      justifyContent: "space-between",
    },
    cardInformationRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    spaceBetween: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    mediumRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    billingAddressError: {
      marginTop: spacing.large,
    },
  });
};
